import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Dialog } from "@headlessui/react";
import DatePickerInput from "../../components/controls/DatePickerInput";
import { fromDateOnlyString, toDateOnlyString } from "../../shared/dateOnly";
import { useToast } from "../../contexts/ToastContext";
import EmptyModal from "../../components/modals/EmptyModal";
import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
  makeDeleteApiCall,
} from "../../shared/makeApiCall";
import type { PaginatedResponse, SelectOption } from "../../types/api";

type Frequency = "weekly" | "biweekly" | "monthly";
interface Person {
  id: number;
  first_name: string;
  last_name: string;
}
interface Customer {
  id: number;
  company_name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  email?: string | null;
  group_id: number | null;
  group_name: string | null;
}
interface Schedule {
  frequency: Frequency;
  day_of_week: number | null;
  day_of_month: number | null;
  week_of_month: number | null;
  start_date: string;
  end_date: string | null;
}
interface Stop {
  customer_id: number;
  notes: string | null;
  schedule: Schedule | null;
  one_time_date: string | null;
}
interface RoutePlan {
  id: number;
  name: string;
  salesperson_id: number | null;
  stops: Stop[];
}
interface RouteRow {
  id: number;
  name: string;
  next_visit_date: string | null;
  salesperson: Person | null;
  customer_count: number;
}
interface Occurrence {
  route_id?: number;
  route_name?: string;
  salesperson_first_name?: string | null;
  salesperson_last_name?: string | null;
  company_name?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  id: number;
  customer_id: number;
  schedule_id: number | null;
  visit_date: string;
  status: string;
  notes: string | null;
}
interface Draft {
  mode: "none" | "once" | Frequency;
  start_date: string;
  end_date: string;
  one_time_date: string;
  day_of_week: number;
  day_of_month: number | "";
  week_of_month: number;
  monthly_mode: "day" | "weekday";
}
const emptyDraft = (): Draft => ({
  mode: "none",
  start_date: "",
  end_date: "",
  one_time_date: "",
  day_of_week: 1,
  day_of_month: 1,
  week_of_month: 1,
  monthly_mode: "day",
});
const schedulePayload = (draft: Draft): Schedule | null =>
  ["none", "once"].includes(draft.mode)
    ? null
    : {
        frequency: draft.mode as Frequency,
        start_date: draft.start_date,
        end_date: draft.end_date || null,
        day_of_week:
          draft.mode !== "monthly" || draft.monthly_mode === "weekday" ? draft.day_of_week : null,
        day_of_month:
          draft.mode === "monthly" && draft.monthly_mode === "day"
            ? draft.day_of_month || null
            : null,
        week_of_month:
          draft.mode === "monthly" && draft.monthly_mode === "weekday" ? draft.week_of_month : null,
      };
interface Options {
  customers: Customer[];
  salespeople: Person[];
}
interface Props {
  logout: () => void;
  userType: number;
}
const frequencies = [
  { value: "weekly" as const, label: "Weekly" },
  { value: "biweekly" as const, label: "Biweekly (every 2 weeks)" },
  { value: "monthly" as const, label: "Monthly" },
];
const visitModes = [
  { value: "none", label: "No visits scheduled" },
  { value: "once", label: "One-time visit" },
  ...frequencies,
];
const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(
  (label, value) => ({ value, label })
);
const monthWeeks = ["First", "Second", "Third", "Fourth", "Fifth"].map((label, index) => ({
  value: index + 1,
  label,
}));
const monthlyPatterns = [
  { value: "day", label: "Day of month" },
  { value: "weekday", label: "Weekday occurrence (e.g. second Tuesday)" },
];
const name = (customer: Customer) =>
  customer.company_name ||
  [customer.given_name, customer.family_name].filter(Boolean).join(" ") ||
  `Customer #${customer.id}`;
const personName = (person: Person) => `${person.first_name} ${person.last_name}`;
const dateLabel = (value: string) => new Date(`${value}T00:00:00`).toLocaleDateString();
const button =
  "rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50";
const panel = "apollo-card rounded-xl border border-gray-200 bg-white p-5";
const input =
  "mt-2 w-full rounded-md border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-indigo-600";

export default function RoutePlanner({ logout, userType }: Props) {
  const location = useLocation();
  const editor = location.pathname.replace(/\/+$/, "") !== "/route-planner";
  return editor ? (
    <RouteEditor key={location.pathname} logout={logout} userType={userType} />
  ) : (
    <RoutesList logout={logout} userType={userType} />
  );
}

function RoutesList({ logout, userType }: Props) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PaginatedResponse<RouteRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<RouteRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [notice, setNotice] = useState("");
  const deleteRoute = async () => {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    setDeleteError("");
    const result = await makeDeleteApiCall(`route-planner/routes/${pendingDelete.id}`);
    setDeleting(false);
    if (!result.success) {
      if (result.errorStatus === 401) logout();
      setDeleteError(result.data.message || "Could not delete this route. Please retry.");
      return;
    }
    setPendingDelete(null);
    setNotice("Route deleted.");
    if (data?.data.length === 1 && page > 1) setPage(page - 1);
    else setRetry((value) => value + 1);
  };
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    void makeGetApiCall<PaginatedResponse<RouteRow>>(`route-planner/routes?page=${page}`).then(
      (result) => {
        if (!active) return;
        if (result.success) setData(result.data);
        else {
          if (result.errorStatus === 401) logout();
          setError(result.data.message || "Could not load routes.");
        }
        setLoading(false);
      }
    );
    return () => {
      active = false;
    };
  }, [page, retry, logout]);
  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Route Planner</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage reusable customer routes and their visit schedules.
          </p>
        </div>
        {userType === 1 && (
          <Link to="/route-planner/new" className={button}>
            Add new route
          </Link>
        )}
      </header>
      {notice && (
        <p role="status" className="rounded-lg bg-green-50 p-4 text-sm text-green-700">
          {notice}
        </p>
      )}
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 p-4 text-red-700">
          {error}{" "}
          <button onClick={() => setRetry((value) => value + 1)} className="underline">
            Retry
          </button>
        </div>
      )}
      {loading ? (
        <p role="status">Loading routes…</p>
      ) : (
        !error && (
          <section className={panel}>
            {!data?.data.length ? (
              <p className="py-8 text-center text-sm text-gray-500">
                No routes yet.
                {userType === 1
                  ? " Add a route to start planning customer visits."
                  : " No routes have been assigned to you."}
              </p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="py-3 pr-4">Route</th>
                        <th className="px-4 py-3">Next visit</th>
                        <th className="px-4 py-3">Customers</th>
                        <th className="px-4 py-3">Salesperson</th>
                        {userType === 1 && <th className="px-4 py-3 text-right">Actions</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.data.map((route) => (
                        <tr key={route.id} className="hover:bg-gray-50">
                          <td className="py-4 pr-4">
                            <Link
                              className="font-semibold text-main-purple hover:underline"
                              to={`/route-planner/${route.id}`}
                            >
                              {route.name || `Route #${route.id}`}
                            </Link>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {route.next_visit_date
                              ? dateLabel(route.next_visit_date)
                              : "No visits scheduled"}
                          </td>

                          <td className="px-4 py-4 tabular-nums">{route.customer_count}</td>
                          <td className="px-4 py-4">
                            {route.salesperson ? personName(route.salesperson) : "Unassigned"}
                          </td>
                          {userType === 1 && (
                            <td className="px-4 py-4 text-right whitespace-nowrap">
                              <div className="flex justify-end gap-3">
                                <Link
                                  to={`/route-planner/${route.id}`}
                                  aria-label={`Edit ${route.name || `Route #${route.id}`}`}
                                  className="inline-flex rounded-md border border-gray-300 px-3 py-2 font-medium text-main-purple hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-600"
                                >
                                  Edit
                                </Link>
                                <button
                                  type="button"
                                  aria-label={`Delete ${route.name || `Route #${route.id}`}`}
                                  onClick={() => {
                                    setPendingDelete(route);
                                    setDeleteError("");
                                  }}
                                  disabled={deleting}
                                  className="inline-flex rounded-md border border-red-200 px-3 py-2 font-medium text-red-600 hover:bg-red-50 focus-visible:ring-2 focus-visible:ring-red-600 disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {data.last_page > 1 && (
                  <nav
                    aria-label="Route pages"
                    className="mt-5 flex items-center justify-between text-sm"
                  >
                    <button
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                      className="rounded border p-2 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span>
                      Page {page} of {data.last_page}
                    </span>
                    <button
                      disabled={page >= data.last_page}
                      onClick={() => setPage(page + 1)}
                      className="rounded border p-2 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                )}
              </>
            )}
          </section>
        )
      )}
      <EmptyModal
        open={pendingDelete !== null}
        dismissible={!deleting}
        setOpen={() => {
          if (!deleting) setPendingDelete(null);
        }}
        childComponent={
          <div>
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              Delete route?
            </Dialog.Title>
            <p className="mt-3 text-sm text-gray-600">
              Delete “{pendingDelete?.name || `Route #${pendingDelete?.id}`}”? Future scheduled
              visits will be removed from the calendar. Completed visits and history will be
              preserved.
            </p>
            {deleteError && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                {deleteError}
              </p>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setPendingDelete(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void deleteRoute()}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-50"
              >
                {deleting ? "Deleting…" : "Delete route"}
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
}

function RouteEditor({ logout, userType }: Props) {
  const { showSuccess } = useToast();
  const [visitsRevision, setVisitsRevision] = useState(0);
  const { routeId } = useParams();
  const isNew = !routeId;
  const admin = userType === 1;
  const navigate = useNavigate();
  const [options, setOptions] = useState<Options>({ customers: [], salespeople: [] });
  const [selected, setSelected] = useState<number[]>([]);
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});
  const [routeName, setRouteName] = useState("");
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [mixedSchedules, setMixedSchedules] = useState(false);
  const [salesperson, setSalesperson] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState<number | null>(null);
  const [customerPage, setCustomerPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const load = async () => {
      const [choices, plan] = await Promise.all([
        makeGetApiCall<Options>("route-planner/options"),
        routeId
          ? makeGetApiCall<RoutePlan>(`route-planner/routes/${routeId}`)
          : Promise.resolve(null),
      ]);
      if (!active) return;
      const failure = !choices.success ? choices : plan && !plan.success ? plan : null;
      if (failure) {
        if (failure.errorStatus === 401) logout();
        setError(failure.data.message || "Could not load this route. Please retry.");
      } else if (choices.success) {
        setOptions(choices.data);
        if (plan?.success) {
          setRouteName(plan.data.name || `Route #${plan.data.id}`);
          setSelected(plan.data.stops.map((stop) => stop.customer_id));
          setNotes(
            Object.fromEntries(plan.data.stops.map((stop) => [stop.customer_id, stop.notes ?? ""]))
          );
          const stop = plan.data.stops[0];
          setDraft({
            mode: stop?.schedule?.frequency ?? (stop?.one_time_date ? "once" : "none"),
            start_date: stop?.schedule?.start_date ?? "",
            end_date: stop?.schedule?.end_date ?? "",
            one_time_date: stop?.one_time_date ?? "",
            day_of_week: stop?.schedule?.day_of_week ?? 1,
            day_of_month: stop?.schedule?.day_of_month ?? 1,
            week_of_month: stop?.schedule?.week_of_month ?? 1,
            monthly_mode: stop?.schedule?.week_of_month != null ? "weekday" : "day",
          });
          const scheduleKey = (stop?: Stop) =>
            JSON.stringify([
              stop?.schedule && [
                stop.schedule.frequency,
                stop.schedule.start_date,
                stop.schedule.end_date,
                stop.schedule.day_of_week,
                stop.schedule.day_of_month,
                stop.schedule.week_of_month,
              ],
              stop?.one_time_date,
            ]);
          const firstSchedule = scheduleKey(stop);
          setMixedSchedules(plan.data.stops.some((item) => scheduleKey(item) !== firstSchedule));
          setSalesperson(plan.data.salesperson_id);
        }
        setLoaded(true);
      }
      setLoading(false);
    };
    void load();
    return () => {
      active = false;
    };
  }, [routeId, logout, retry]);
  const groups = useMemo(
    () =>
      Array.from(
        new Map(
          options.customers
            .filter((customer) => customer.group_id !== null)
            .map((customer) => [
              customer.group_id,
              { value: customer.group_id!, label: customer.group_name || "Unnamed group" },
            ])
        ).values()
      ),
    [options.customers]
  );
  const selectedSalesperson = options.salespeople.find((person) => person.id === salesperson);
  const query = search.trim().toLowerCase();
  const customers = useMemo(
    () =>
      options.customers.filter(
        (customer) =>
          (group === null || customer.group_id === group) &&
          `${name(customer)} ${customer.email ?? ""}`.toLowerCase().includes(query)
      ),
    [options.customers, group, query]
  );
  const customerPages = Math.max(1, Math.ceil(customers.length / 10));
  const currentCustomerPage = Math.min(customerPage, customerPages);
  const customerOffset = (currentCustomerPage - 1) * 10;
  const visibleCustomers = customers.slice(customerOffset, customerOffset + 10);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if (!selected.length) {
      setError("Choose at least one customer.");
      return;
    }
    if (!routeName.trim()) {
      setError("Enter a route name.");
      return;
    }
    if (
      draft.mode === "monthly" &&
      draft.monthly_mode === "day" &&
      (!Number.isInteger(draft.day_of_month) ||
        Number(draft.day_of_month) < 1 ||
        Number(draft.day_of_month) > 31)
    ) {
      setError("Choose a day of the month between 1 and 31.");
      return;
    }
    if (
      draft.mode === "once"
        ? !draft.one_time_date
        : draft.mode !== "none" &&
          (!draft.start_date || (draft.end_date !== "" && draft.end_date < draft.start_date))
    ) {
      setError(
        "Choose a date for the route schedule and make sure the end date follows the start date."
      );
      return;
    }
    const schedule = schedulePayload(draft);
    const stops = selected.map((id) => ({
      customer_id: id,
      notes: notes[id]?.trim() || null,
      schedule,
      one_time_date: draft.mode === "once" ? draft.one_time_date || null : null,
    }));
    setSaving(true);
    const payload = { name: routeName.trim(), salesperson_id: salesperson, stops };
    const result = isNew
      ? await makePostApiCall<RoutePlan>("route-planner/routes", payload)
      : await makePutApiCall<RoutePlan>(`route-planner/routes/${routeId}`, payload);
    setSaving(false);
    if (result.success) {
      showSuccess("Route saved.");
      if (isNew) navigate(`/route-planner/${result.data.id}`, { replace: true });
      else {
        setMixedSchedules(false);
        setVisitsRevision((value) => value + 1);
      }
    } else {
      if (result.errorStatus === 401) logout();
      setError(
        result.data.message || "Could not save the route. Your selections and notes are preserved."
      );
    }
  };
  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header>
        <Link to="/route-planner" className="text-sm text-main-purple hover:underline">
          ← All routes
        </Link>
        <h1 className="mt-2 text-xl font-semibold text-gray-900">
          {isNew ? "New route" : routeName || `Route #${routeId}`}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Select customers and choose one shared visit schedule for the route.
        </p>
      </header>
      {error && (
        <div role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {error}
          {!loaded && (
            <button
              type="button"
              onClick={() => setRetry((value) => value + 1)}
              className="ml-3 underline"
            >
              Retry
            </button>
          )}
        </div>
      )}
      {loading ? (
        <p role="status">Loading route…</p>
      ) : (
        loaded && (
          <form onSubmit={save} className="space-y-6">
            <section className={panel}>
              <label htmlFor="route-name" className="block text-sm font-medium text-gray-900">
                Route name
              </label>
              <input
                id="route-name"
                name="name"
                maxLength={255}
                value={routeName}
                readOnly={!admin}
                disabled={saving}
                onChange={(event) => setRouteName(event.target.value)}
                placeholder="e.g. North Toronto Monday"
                className={input}
              />
              <div className="mt-5">
                <h2 className="font-semibold text-gray-900">Route assignment</h2>
                <div className="mt-4 max-w-md">
                  <div>
                    <label
                      htmlFor="salesperson"
                      className="mb-2 block text-sm font-medium text-gray-900"
                    >
                      Salesperson
                    </label>
                    <Select
                      inputId="salesperson"
                      options={options.salespeople.map((person) => ({
                        value: person.id,
                        label: personName(person),
                      }))}
                      value={
                        salesperson
                          ? {
                              value: salesperson,
                              label: selectedSalesperson
                                ? personName(selectedSalesperson)
                                : "Assigned salesperson",
                            }
                          : null
                      }
                      onChange={(item: SelectOption | null) => setSalesperson(item?.value ?? null)}
                      isClearable
                      placeholder="Unassigned"
                      isDisabled={!admin || saving}
                    />
                  </div>
                </div>
              </div>
            </section>
            <section className={panel}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-semibold text-gray-900">Customers to visit</h2>
                <span className="text-sm text-gray-500">
                  {selected.length} selected · {customers.length} matching customers
                </span>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-gray-900">
                  Search customers
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setCustomerPage(1);
                    }}
                    className={input}
                    placeholder="Name or email"
                  />
                </label>
                <div>
                  <label
                    htmlFor="customer-group"
                    className="mb-2 block text-sm font-medium text-gray-900"
                  >
                    Customer group
                  </label>
                  <Select
                    inputId="customer-group"
                    options={groups}
                    value={groups.find((item) => item.value === group) ?? null}
                    onChange={(item: SelectOption | null) => {
                      setGroup(item?.value ?? null);
                      setCustomerPage(1);
                    }}
                    isClearable
                    placeholder="All customer groups"
                  />
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Filtering keeps your selected customers and visit notes.
              </p>
              {selected.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-4 border-t border-gray-100 pt-4 text-sm">
                  {[false, true].map((collapse) => (
                    <button
                      key={String(collapse)}
                      type="button"
                      className="font-medium text-main-purple hover:underline focus-visible:outline focus-visible:outline-main-purple"
                      onClick={() =>
                        setCollapsed(Object.fromEntries(selected.map((id) => [id, collapse])))
                      }
                    >
                      {collapse ? "Collapse all" : "Expand all"}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-4 space-y-3">
                {visibleCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className={`rounded-lg border p-4 sm:p-5 ${selected.includes(customer.id) ? "border-purple-200 bg-purple-50/20" : "border-gray-200"}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <label className="flex min-w-0 flex-1 items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selected.includes(customer.id)}
                          disabled={!admin || saving}
                          onChange={(event) =>
                            setSelected((ids) =>
                              event.target.checked
                                ? [...ids, customer.id]
                                : ids.filter((id) => id !== customer.id)
                            )
                          }
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-main-purple focus:ring-indigo-600"
                        />
                        <span>
                          <span className="block text-sm font-semibold text-gray-900">
                            {name(customer)}
                          </span>
                          <span className="block text-xs text-gray-500">
                            {customer.group_name || "No customer group"}
                            {customer.email ? ` · ${customer.email}` : ""}
                          </span>
                        </span>
                      </label>
                      {selected.includes(customer.id) && (
                        <button
                          type="button"
                          aria-expanded={!collapsed[customer.id]}
                          aria-controls={`customer-visit-${customer.id}`}
                          aria-label={`${collapsed[customer.id] ? "Expand" : "Collapse"} visit details for ${name(customer)}`}
                          className="rounded px-2 py-1 text-sm font-medium text-main-purple hover:bg-purple-50 focus-visible:outline focus-visible:outline-main-purple"
                          onClick={() =>
                            setCollapsed((values) => ({
                              ...values,
                              [customer.id]: !values[customer.id],
                            }))
                          }
                        >
                          {collapsed[customer.id] ? "Show details ▾" : "Hide details ▴"}
                        </button>
                      )}
                    </div>
                    <Link
                      to={`/customers/${customer.id}?tab=visits`}
                      className="ml-7 mt-2 inline-block text-sm font-medium text-main-purple hover:underline focus-visible:outline focus-visible:outline-main-purple"
                    >
                      View customer visit information
                    </Link>
                    {selected.includes(customer.id) && collapsed[customer.id] && (
                      <p className="ml-7 mt-2 text-xs text-gray-500">
                        {notes[customer.id]?.trim() ? "Visit notes added" : "No visit notes"}
                      </p>
                    )}
                    {selected.includes(customer.id) && (
                      <div id={`customer-visit-${customer.id}`} hidden={collapsed[customer.id]}>
                        <label className="ml-7 mt-4 block text-sm text-gray-600">
                          Visit notes for {name(customer)}
                          <textarea
                            maxLength={5000}
                            rows={2}
                            value={notes[customer.id] ?? ""}
                            readOnly={!admin}
                            disabled={saving}
                            onChange={(event) =>
                              setNotes((values) => ({
                                ...values,
                                [customer.id]: event.target.value,
                              }))
                            }
                            className={input}
                            placeholder="Delivery entrance, preferred visit time, or other instructions"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {customers.length > 10 && (
                <nav
                  aria-label="Customer pagination"
                  className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4 text-sm"
                >
                  <span className="text-gray-500">
                    Showing {customerOffset + 1}–{Math.min(customerOffset + 10, customers.length)}{" "}
                    of {customers.length}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={currentCustomerPage === 1}
                      onClick={() => setCustomerPage(currentCustomerPage - 1)}
                      className="rounded-md border border-gray-300 px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous customers
                    </button>
                    <span className="text-gray-600">
                      Page {currentCustomerPage} of {customerPages}
                    </span>
                    <button
                      type="button"
                      disabled={currentCustomerPage === customerPages}
                      onClick={() => setCustomerPage(currentCustomerPage + 1)}
                      className="rounded-md border border-gray-300 px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next customers
                    </button>
                  </div>
                </nav>
              )}
              {!customers.length && (
                <p className="py-8 text-center text-sm text-gray-500">
                  {options.customers.length
                    ? "No customers match these filters."
                    : "No customers available."}
                </p>
              )}
            </section>
            <section className={panel} aria-label="Route schedule">
              <h2 className="font-semibold text-gray-900">Route schedule</h2>
              <p className="mt-1 text-sm text-gray-500">
                Every customer on this route will use the same visit date and frequency.
              </p>
              {mixedSchedules && (
                <p role="status" className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                  This route has different customer schedules. Review the schedule below, loaded
                  from the first customer. Saving will apply it to every selected customer.
                </p>
              )}
              <RouteSchedule value={draft} disabled={!admin || saving} onChange={setDraft} />
            </section>
            {routeId && (
              <RouteOccurrences
                routeId={routeId}
                customers={options.customers}
                logout={logout}
                revision={visitsRevision}
              />
            )}
            <div className="flex items-center justify-end gap-4">
              <Link to="/route-planner" className="text-sm text-gray-600 hover:underline">
                {admin ? "Cancel" : "Back to routes"}
              </Link>
              {admin && (
                <button type="submit" disabled={saving} className={button}>
                  {saving ? "Saving…" : isNew ? "Create route" : "Save route"}
                </button>
              )}
            </div>
          </form>
        )
      )}
    </div>
  );
}

function RouteSchedule({
  value,
  disabled,
  onChange,
}: {
  value: Draft;
  disabled: boolean;
  onChange: (draft: Draft) => void;
}) {
  const set = (patch: Partial<Draft>) => onChange({ ...value, ...patch });
  const dateControl = (label: string, field: "start_date" | "end_date" | "one_time_date") => (
    <label className="block text-sm text-gray-600">
      {label} for this route
      <DatePickerInput
        name={field}
        aria-label={`${label} for this route`}
        value={fromDateOnlyString(value[field])}
        onChange={({ target }) => set({ [field]: toDateOnlyString(target.value) ?? "" })}
        showIcon
        showButtonBar
        readOnlyInput
        className="date-picker mt-2 w-full"
        disabled={disabled}
      />
    </label>
  );
  return (
    <fieldset disabled={disabled} className="mt-4 space-y-3 rounded-lg border border-gray-200 p-4">
      <legend className="px-1 text-sm font-medium text-gray-900">
        Visit schedule for this route
      </legend>
      <Select
        aria-label="Visit type for this route"
        options={visitModes}
        value={visitModes.find((mode) => mode.value === value.mode)}
        onChange={(mode: SelectOption<string> | null) => {
          if (mode) set({ mode: mode.value as Draft["mode"] });
        }}
        isDisabled={disabled}
        isSearchable={false}
      />
      {value.mode === "once" && dateControl("Visit date", "one_time_date")}
      {!["none", "once"].includes(value.mode) && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {dateControl("Start date", "start_date")}
            {dateControl("End date (optional)", "end_date")}
          </div>
          {value.mode === "monthly" && (
            <Select
              aria-label="Monthly pattern for this route"
              options={monthlyPatterns}
              value={monthlyPatterns.find((option) => option.value === value.monthly_mode)}
              onChange={(option: SelectOption<string> | null) => {
                if (option) set({ monthly_mode: option.value as Draft["monthly_mode"] });
              }}
              isDisabled={disabled}
            />
          )}
          {value.mode === "monthly" && value.monthly_mode === "day" ? (
            <label className="block text-sm text-gray-600">
              Day of month for this route
              <input
                type="number"
                min={1}
                max={31}
                value={value.day_of_month}
                onChange={(event) => {
                  const day = event.target.value;
                  if (day === "") set({ day_of_month: "" });
                  else if (/^\d+$/.test(day) && Number(day) >= 1 && Number(day) <= 31)
                    set({ day_of_month: Number(day) });
                }}
                className={input}
              />
            </label>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {value.mode === "monthly" && (
                <Select
                  aria-label="Week of month for this route"
                  options={monthWeeks}
                  value={monthWeeks[value.week_of_month - 1]}
                  onChange={(option: SelectOption | null) => {
                    if (option) set({ week_of_month: option.value });
                  }}
                  isDisabled={disabled}
                />
              )}
              <Select
                aria-label="Weekday for this route"
                options={weekdays}
                value={weekdays[value.day_of_week]}
                onChange={(option: SelectOption | null) => {
                  if (option) set({ day_of_week: option.value });
                }}
                isDisabled={disabled}
              />
            </div>
          )}
          <p className="text-xs text-gray-500">
            {value.mode === "biweekly"
              ? "Every two weeks, anchored to the first matching weekday on or after the start date."
              : value.mode === "monthly"
                ? "Days 29–31 use the last day of shorter months. A fifth weekday is skipped when absent."
                : "Repeats on the selected weekday from the start date."}{" "}
            Visits are generated for the current month and next month.
          </p>
        </>
      )}
    </fieldset>
  );
}

export function RouteCalendar({ logout }: { logout: () => void }) {
  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Route Calendar</h1>
        <p className="mt-1 text-sm text-gray-500">
          View scheduled customer visits across your routes. Select a day for route details.
        </p>
      </header>
      <RouteOccurrences logout={logout} />
    </div>
  );
}

function RouteOccurrences({
  revision = 0,
  routeId,
  customers = [],
  logout,
}: {
  revision?: number;
  routeId?: string;
  customers?: Customer[];
  logout: () => void;
}) {
  const [month, setMonth] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [selected, setSelected] = useState("");
  const [data, setData] = useState<Occurrence[] | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const customerNames = useMemo(
    () => new Map(customers.map((customer) => [customer.id, name(customer)])),
    [customers]
  );
  const from = toDateOnlyString(new Date(month.getFullYear(), month.getMonth(), 1))!;
  const through = toDateOnlyString(new Date(month.getFullYear(), month.getMonth() + 1, 0))!;
  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    async function load() {
      const visits: Occurrence[] = [];
      let page = 1;
      while (active) {
        const result = await makeGetApiCall<PaginatedResponse<Occurrence>>(
          `${routeId ? `route-planner/routes/${routeId}/visits` : "route-planner/calendar"}?page=${page}&from_date=${from}&through_date=${through}`
        );
        if (!active) return;
        if (!result.success) {
          if (result.errorStatus === 401) logout();
          setError(result.data.message || "Could not load calendar visits.");
          return;
        }
        visits.push(...result.data.data.filter((visit) => visit.status !== "cancelled"));
        if (page >= result.data.last_page) break;
        page++;
      }
      if (active) setData(visits);
    }
    void load();
    return () => {
      active = false;
    };
  }, [routeId, from, through, retry, logout, revision]);
  const byDate = useMemo(() => {
    const visits = new Map<string, Occurrence[]>();
    data?.forEach((visit) => {
      const date = visit.visit_date.slice(0, 10);
      if (!visits.has(date)) visits.set(date, []);
      visits.get(date)!.push(visit);
    });
    return visits;
  }, [data]);
  const selectedVisits = byDate.get(selected) ?? [];
  const visitGroups = new Map<number | undefined, Occurrence[]>();
  selectedVisits.forEach((visit) => {
    if (!visitGroups.has(visit.route_id)) visitGroups.set(visit.route_id, []);
    visitGroups.get(visit.route_id)!.push(visit);
  });
  return (
    <section className={panel} aria-label="Calendar visits">
      <h2 className="font-semibold text-gray-900">Calendar visits</h2>
      {error ? (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error}{" "}
          <button
            type="button"
            className="underline"
            onClick={() => setRetry((value) => value + 1)}
          >
            Retry visits
          </button>
        </p>
      ) : !data ? (
        <p role="status" className="mt-3 text-sm text-gray-500">
          Loading calendar visits…
        </p>
      ) : (
        <>
          <DatePickerInput
            name="visit-calendar"
            aria-label="Visit calendar"
            inline
            value={fromDateOnlyString(selected)}
            viewDate={month}
            showOtherMonths={false}
            onViewDateChange={({ value }) => {
              setMonth(value);
              setSelected("");
            }}
            onChange={({ target }) => setSelected(toDateOnlyString(target.value) ?? "")}
            pt={{
              panel: { className: "mt-4 w-full border-0 p-0" },
              table: { className: "w-full table-fixed" },
              day: { className: "border border-gray-200 p-0" },
              dayLabel: { style: { width: "100%", height: "auto", borderRadius: "0.5rem" } },
            }}
            dateTemplate={({ year, month, day }) => {
              const date = toDateOnlyString(new Date(year, month, day))!;
              const visits = byDate.get(date) ?? [];
              const count = visits.length;
              const routes = routeId
                ? []
                : Array.from(new Set(visits.map((visit) => visit.route_name))).filter(Boolean);
              return (
                <span
                  aria-label={`${date}: ${count} visits`}
                  className="block min-h-[76px] w-full p-1 text-left text-sm sm:min-h-[100px] sm:p-3"
                >
                  <span className="block tabular-nums">{day}</span>
                  {routes.slice(0, 2).map((route) => (
                    <span key={route} className="mt-1 block truncate text-xs" title={route}>
                      {route}
                    </span>
                  ))}
                  {routes.length > 2 && (
                    <span className="block text-xs text-gray-500">+{routes.length - 2} routes</span>
                  )}
                  {count > 0 && (
                    <span className="mt-2 block rounded bg-purple-50 px-1 text-xs font-medium text-main-purple">
                      {count}{" "}
                      <span className="hidden sm:inline">{count === 1 ? "visit" : "visits"}</span>
                    </span>
                  )}
                </span>
              );
            }}
          />
          {!data.length && <p className="mt-3 text-sm text-gray-500">No visits this month.</p>}
          <div className="mt-4" aria-live="polite">
            {selected ? (
              <>
                <h3 className="text-sm font-semibold text-gray-900">
                  Visits on {dateLabel(selected)}
                </h3>
                {!selectedVisits.length ? (
                  <p className="mt-2 text-sm text-gray-500">No visits on this day.</p>
                ) : (
                  <div className="mt-4 space-y-5">
                    {Array.from(visitGroups, ([id, visits]) => (
                      <section
                        key={id ?? "route"}
                        className="overflow-hidden rounded-lg border border-gray-200"
                      >
                        {!routeId && (
                          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3">
                            <h4 className="text-sm font-semibold text-gray-900">
                              <Link
                                to={`/route-planner/${id}`}
                                className="text-main-purple hover:underline"
                              >
                                {visits[0].route_name || `Route #${id}`}
                              </Link>
                            </h4>
                            <span className="text-sm text-gray-600">
                              <strong className="font-semibold text-gray-900">Salesperson:</strong>{" "}
                              {[visits[0].salesperson_first_name, visits[0].salesperson_last_name]
                                .filter(Boolean)
                                .join(" ") || "Unassigned"}
                            </span>
                          </div>
                        )}
                        <ul className="divide-y divide-gray-200 px-4">
                          {visits.map((visit) => (
                            <li
                              key={visit.id}
                              className="flex flex-wrap justify-between gap-2 py-3 text-sm"
                            >
                              <Link
                                to={`/customers/${visit.customer_id}?tab=visits&date=${visit.visit_date.slice(0, 10)}&visit=${visit.id}`}
                                className="text-main-purple hover:underline"
                              >
                                {visit.company_name ||
                                  [visit.given_name, visit.family_name].filter(Boolean).join(" ") ||
                                  customerNames.get(visit.customer_id) ||
                                  `Customer #${visit.customer_id}`}
                              </Link>
                              <span className="capitalize text-gray-500">
                                {visit.status} · {visit.schedule_id ? "Recurring" : "One-time"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </section>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500">Select a day to see its customer visits.</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
